import type { FC, ReactNode } from "react";
import { I18nextProvider } from "react-i18next";

import { AviaryProvider } from "@aviary/utils";
import {
  LazyHotReloadIndicator,
  LazyReactAxe,
  LoadingProvider,
  Router,
  PageViewLogger,
} from "@shared/components";
import {
  ExperimentProvider,
  FlippersProvider,
  GlobalContextProvider as SharedGlobalContextProvider,
  GlobalContextUserRole,
} from "@shared/context";
import type { Flipper } from "@shared/types/flipper";
import { GlobalContextProvider } from "@unauthenticated/shared/context";
import { ApolloClientProvider } from "@unauthenticated/signupSignin/components/ApolloClientProvider";
import { i18n } from "@unauthenticated/signupSignin/locales";

interface Props {
  emailRegex: string;
  treatmentPlanTerm?: string;
  currentCountry: string;
  flippers: Flipper[];
  isFromWellevate?: boolean;
  isFromEmersonEcologics?: boolean;
  isEmerson?: boolean;
  e2e: boolean;
  analyticsAnonymousId: string;
  children: ReactNode;
}

const Providers: FC<Props> = ({
  children,
  emailRegex,
  treatmentPlanTerm,
  currentCountry,
  flippers,
  isFromWellevate,
  isFromEmersonEcologics,
  isEmerson,
  e2e,
  analyticsAnonymousId,
}) => {
  return (
    <FlippersProvider flippers={flippers}>
      <LoadingProvider>
        <ApolloClientProvider>
          <Router>
            <I18nextProvider i18n={i18n}>
              <SharedGlobalContextProvider
                country={currentCountry}
                isFromWellevate={isFromWellevate}
                isFromEmersonEcologics={isFromEmersonEcologics}
                isEmerson={isEmerson}
                role={GlobalContextUserRole.UNAUTHENTICATED}
                e2e={e2e}
              >
                <GlobalContextProvider
                  emailRegexString={emailRegex}
                  treatmentPlanTerm={treatmentPlanTerm}
                >
                  <AviaryProvider isWhiteBackground>
                    <ExperimentProvider value={{ analyticsAnonymousId }}>
                      <PageViewLogger isUnauth>{children}</PageViewLogger>
                    </ExperimentProvider>
                  </AviaryProvider>
                </GlobalContextProvider>
              </SharedGlobalContextProvider>
            </I18nextProvider>
          </Router>
          <LazyHotReloadIndicator />
          <LazyReactAxe />
        </ApolloClientProvider>
      </LoadingProvider>
    </FlippersProvider>
  );
};

export { Providers };
